import { adoptAnonymousId } from 'ProdantixSdk/core/identity';
import { adoptSession } from 'ProdantixSdk/core/session';
import type { Consent, StorageAdapter } from 'ProdantixSdk/core/types';
import { codePointLength } from 'ProdantixSdk/core/validation';
import { domainCandidates } from 'ProdantixSdk/web/cookie-storage';

export const CARRY_PARAMETER = '_pdx';

const SEPARATOR = '~';
const MAX_ID_LENGTH = 200;
const EPOCH_SHAPE = /^\d+$/;

export interface Carry {
  anonymousId: string;
  sessionId: string;
  sessionStartedAt: number;
}

export interface AnchorLike {
  href: string;
}

export interface LocationLike {
  hash: string;
  hostname: string;
  pathname: string;
  search: string;
}

export interface HistoryLike {
  readonly state: unknown;
  replaceState(data: unknown, unused: string, url?: string | null): void;
}

export interface DocumentLike {
  addEventListener(type: string, listener: (event: unknown) => void, capture?: boolean): void;
  location?: { hostname: string } | null;
  removeEventListener(type: string, listener: (event: unknown) => void, capture?: boolean): void;
}

export interface CarryClient {
  readonly anonymousId: string;
  consent?(): Consent | null;
  readonly sessionId: string | undefined;
  readonly sessionStartedAt: number | undefined;
}

export interface AdoptionSeams {
  history: HistoryLike | undefined;
  location: LocationLike | undefined;
  namespace: string;
  now: number;
  shared: StorageAdapter;
  storage: StorageAdapter;
}

interface ElementLike {
  href?: unknown;
  parentElement?: ElementLike | null;
  tagName?: unknown;
}

export function decorateLink(anchor: AnchorLike, carry: Carry, linkedDomains: readonly string[], currentHost: string): void {
  const url = httpUrl(anchor.href);
  if (url === undefined || queryParts(url.search).some(isCarryPart)) {
    return;
  }
  const linked = linkedDomainOf(url.hostname, linkedDomains);
  if (linked === undefined || linked === linkedDomainOf(currentHost, linkedDomains)) {
    return;
  }
  url.search = `${url.search}${url.search ? '&' : '?'}${CARRY_PARAMETER}=${formatCarry(carry)}`;
  anchor.href = url.href;
}

export function formatCarry(carry: Carry): string {
  return [carry.anonymousId, carry.sessionId, String(carry.sessionStartedAt)].map(encodePart).join(SEPARATOR);
}

export function readCarry(search: string): Carry | undefined {
  const part = queryParts(search).find(isCarryPart);
  if (part === undefined) {
    return undefined;
  }
  const pieces = valueOf(part).split(SEPARATOR);
  if (pieces.length !== 3) {
    return undefined;
  }
  const anonymousId = decodePart(pieces[0]);
  const sessionId = decodePart(pieces[1]);
  const startedAt = decodePart(pieces[2]);
  if (!isIdShape(anonymousId) || !isIdShape(sessionId) || !isEpochShape(startedAt)) {
    return undefined;
  }
  return { anonymousId, sessionId, sessionStartedAt: Number(startedAt) };
}

export function stripCarry(history: HistoryLike, location: LocationLike): void {
  const parts = queryParts(location.search);
  const kept = parts.filter(part => !isCarryPart(part));
  if (kept.length === parts.length) {
    return;
  }
  const search = kept.length > 0 ? `?${kept.join('&')}` : '';
  history.replaceState(history.state, '', `${location.pathname}${search}${location.hash}`);
}

export function adoptCarry(seams: AdoptionSeams): string | undefined {
  if (seams.location === undefined || typeof seams.location.search !== 'string') {
    return undefined;
  }
  const carry = readCarry(seams.location.search);
  if (carry === undefined) {
    return undefined;
  }
  const previous = adoptAnonymousId(seams.storage, seams.shared, seams.namespace, carry.anonymousId);
  adoptSession(seams.shared, seams.namespace, { id: carry.sessionId, startedAt: carry.sessionStartedAt }, seams.now);
  if (seams.history !== undefined) {
    stripCarry(seams.history, seams.location);
  }
  return previous;
}

export function installLinkCarry(doc: DocumentLike, client: CarryClient, linkedDomains: readonly string[]): () => void {
  if (linkedDomains.length === 0) {
    return () => undefined;
  }
  const handler = (event: unknown): void => {
    const anchor = findAnchor((event as { target?: unknown }).target);
    if (anchor === undefined) {
      return;
    }
    const carry = carryOf(client);
    if (carry === undefined) {
      return;
    }
    decorateLink(anchor, carry, linkedDomains, doc.location?.hostname ?? '');
  };
  doc.addEventListener('click', handler, true);
  doc.addEventListener('auxclick', handler, true);
  return () => {
    doc.removeEventListener('click', handler, true);
    doc.removeEventListener('auxclick', handler, true);
  };
}

function httpUrl(href: string): URL | undefined {
  try {
    const url = new URL(href);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
}

function linkedDomainOf(hostname: string, linkedDomains: readonly string[]): string | undefined {
  const listed = linkedDomains.map(domain => domain.replace(/^\./, '').toLowerCase());
  return domainCandidates(hostname).find(candidate => listed.includes(candidate));
}

function queryParts(search: string): string[] {
  return search
    .replace(/^\?/, '')
    .split('&')
    .filter(part => part !== '');
}

function isCarryPart(part: string): boolean {
  const eq = part.indexOf('=');
  return decodePart(eq === -1 ? part : part.slice(0, eq)) === CARRY_PARAMETER;
}

function valueOf(part: string): string {
  const eq = part.indexOf('=');
  return eq === -1 ? '' : part.slice(eq + 1);
}

function encodePart(part: string): string {
  return encodeURIComponent(part).replace(/~/g, '%7E');
}

function decodePart(part: string): string | undefined {
  try {
    return decodeURIComponent(part);
  } catch {
    return undefined;
  }
}

function isIdShape(value: string | undefined): value is string {
  if (value === undefined) {
    return false;
  }
  const length = codePointLength(value);
  return length >= 1 && length <= MAX_ID_LENGTH;
}

function isEpochShape(value: string | undefined): value is string {
  return value !== undefined && EPOCH_SHAPE.test(value) && Number.isSafeInteger(Number(value));
}

function carryOf(client: CarryClient): Carry | undefined {
  // Carrying a declined visitor's id to another domain would be the join by
  // another route, so the answer is read at click time, not at install time.
  if (client.consent?.()?.identityLink === false) {
    return undefined;
  }
  const sessionId = client.sessionId;
  const sessionStartedAt = client.sessionStartedAt;
  if (sessionId === undefined || sessionStartedAt === undefined) {
    return undefined;
  }
  return { anonymousId: client.anonymousId, sessionId, sessionStartedAt };
}

function isAnchor(node: ElementLike): node is ElementLike & AnchorLike {
  return typeof node.tagName === 'string' && node.tagName.toUpperCase() === 'A' && typeof node.href === 'string';
}

function findAnchor(target: unknown): AnchorLike | undefined {
  let node = typeof target === 'object' ? (target as ElementLike | null) : null;
  while (node) {
    if (isAnchor(node)) {
      return node;
    }
    node = node.parentElement ?? null;
  }
  return undefined;
}
